import API_URLS from '../../../../config/apiConfig';
import { apiGet, apiPatch, apiPost, apiPut } from '../../../../utils/apiUtils';
import type {
  CreateStoreLocationRegisterPayload,
  CreateStoreLocationPayload,
  StoreLocation,
  StoreRegisterInfo,
  StoreLocationRegister,
  StoreLocationType,
  UpdateStoreLocationPayload,
  UpdateStoreLocationRegisterPayload,
} from '../../../../types/features/storeDetails/locations';

type CreateStoreLocationRequest = {
  location_key: string;
  location_name: string;
  location_type: StoreLocationType;
  vending_bin_count?: number;
};

class StoreLocationsService {
  async getStoreLocations(storeId: string): Promise<StoreLocation[]> {
    const response = await apiGet(API_URLS.GET_STORE_LOCATIONS(storeId));

    return Array.isArray(response) ? response as StoreLocation[] : [];
  }

  async getStoreLocationRegisters(storeId: string): Promise<StoreLocationRegister[]> {
    const response = await apiGet(API_URLS.GET_STORE_LOCATION_REGISTERS(storeId));

    return Array.isArray(response) ? response as StoreLocationRegister[] : [];
  }

  async getStoreRegisterInfo(storeId: string): Promise<StoreRegisterInfo> {
    try {
      const response = await apiGet(API_URLS.GET_CSTORE_REGISTER_INFO(storeId));

      return {
        store_id: response?.store_id || storeId,
        store_name: response?.store_name || '',
        register_ids: Array.isArray(response?.register_ids) ? response.register_ids : [],
      };
    } catch (error) {
      const errorRecord = typeof error === 'object' && error !== null ? error as {
        detail?: unknown;
        response?: {
          data?: {
            detail?: unknown;
          };
        };
      } : null;
      const detail = String(
        errorRecord?.detail
        || errorRecord?.response?.data?.detail
        || '',
      );
      const message = error instanceof Error ? error.message : '';

      if (detail.includes('No register_id found') || message.includes('No register_id found')) {
        return {
          store_id: storeId,
          store_name: '',
          register_ids: [],
        };
      }

      throw error;
    }
  }

  async createStoreLocation(storeId: string, payload: CreateStoreLocationRequest): Promise<unknown> {
    const requestBody: CreateStoreLocationPayload = {
      ...payload,
      is_default: false,
    };

    return apiPost(API_URLS.CREATE_STORE_LOCATION(storeId), requestBody);
  }

  async updateStoreLocation(
    storeId: string,
    locationId: string,
    payload: UpdateStoreLocationPayload,
  ): Promise<unknown> {
    return apiPatch(API_URLS.UPDATE_STORE_LOCATION(storeId, locationId), payload);
  }

  async updateStoreLocationRegister(
    storeId: string,
    registerId: string,
    payload: UpdateStoreLocationRegisterPayload,
  ): Promise<unknown> {
    return apiPatch(API_URLS.UPDATE_STORE_LOCATION_REGISTER(storeId, registerId), payload);
  }

  async createStoreLocationRegister(
    storeId: string,
    payload: CreateStoreLocationRegisterPayload,
  ): Promise<unknown> {
    return apiPost(API_URLS.CREATE_STORE_LOCATION_REGISTER(storeId), payload);
  }
}

const storeLocationsService = new StoreLocationsService();

export default storeLocationsService;
